import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';
import type { Fulfilment } from './FulfilmentSummary';

/**
 * Correct ONE sailing.
 *
 * A slipped ETA is the most common correction in this whole module. Before
 * shipments were batched it meant overwriting the indent's single vessel
 * record, which moved every container on the order — including the ones
 * already sitting in the depot. Here it moves exactly the containers on this
 * sailing and leaves the others alone.
 */

type Batch = Fulfilment['batches'][number];

const dateInput = (iso: string | null) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

const STATUSES = ['Awaiting Departure', 'On Water', 'Arrived', 'Cleared', 'Delivered', 'Cancelled'] as const;

export default function BatchEditModal({ batch, indentId, onClose }: {
  batch: Batch;
  indentId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    shippingLine: batch.shippingLine || '',
    vesselName: batch.vesselName || '',
    voyageNo: batch.voyageNo || '',
    blNumber: batch.blNumber || '',
    etdDeparture: dateInput(batch.etdDeparture),
    etaArrival: dateInput(batch.etaArrival),
    assignedLocalAgent: batch.assignedLocalAgent || '',
    status: batch.status,
    notes: '',
  });
  const set = (k: keyof typeof form) => (v: string) => setForm((p) => ({ ...p, [k]: v }));

  const etaMoved = dateInput(batch.etaArrival) !== form.etaArrival;

  const save = useMutation({
    mutationFn: () => api.patch(`/api/procurement/logistics/batches/${batch.id}`, {
      ...form,
      // Empty means "clear it", which is different from "leave it alone" —
      // send null so the server can tell the two apart.
      etdDeparture: form.etdDeparture || null,
      etaArrival: form.etaArrival || null,
    }),
    onSuccess: () => {
      toast.success(`Batch ${batch.batchNo} updated`);
      qc.invalidateQueries({ queryKey: ['procurement', 'fulfilment', indentId] });
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.message || 'Could not update the sailing'),
  });

  const lbl = 'text-[10px] uppercase tracking-wider font-bold text-slate-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <div className="font-bold text-slate-900">Batch {batch.batchNo} &middot; {indentId}</div>
            <div className="text-[11px] text-slate-500">
              {batch.quantity} container{batch.quantity === 1 ? '' : 's'}
              {batch.receivedCount > 0 && ` · ${batch.receivedCount} already received`}
            </div>
          </div>
          <button className="text-2xl leading-none text-slate-400 hover:text-slate-700" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="space-y-3 overflow-y-auto p-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <div className={lbl}>On this sailing</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {batch.containerNos.length
                ? batch.containerNos.map((n) => (
                  <span key={n} className="rounded bg-white px-1.5 py-0.5 font-mono text-[11px] text-slate-700 ring-1 ring-slate-200">{n}</span>
                ))
                : <span className="text-[11px] text-slate-400">Serials not recorded yet.</span>}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div><label className={lbl}>Shipping line</label><input className="input mt-1" value={form.shippingLine} onChange={(e) => set('shippingLine')(e.target.value)} /></div>
            <div><label className={lbl}>Vessel name</label><input className="input mt-1" value={form.vesselName} onChange={(e) => set('vesselName')(e.target.value)} /></div>
            <div><label className={lbl}>Voyage no.</label><input className="input mt-1 font-mono" value={form.voyageNo} onChange={(e) => set('voyageNo')(e.target.value)} /></div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><label className={lbl}>ETD (departure)</label><input type="date" className="input mt-1" value={form.etdDeparture} onChange={(e) => set('etdDeparture')(e.target.value)} /></div>
            <div><label className={lbl}>ETA (arrival)</label><input type="date" className="input mt-1" value={form.etaArrival} onChange={(e) => set('etaArrival')(e.target.value)} /></div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><label className={lbl}>BL number</label><input className="input mt-1 font-mono" value={form.blNumber} onChange={(e) => set('blNumber')(e.target.value)} /></div>
            <div><label className={lbl}>Local agent</label><input className="input mt-1" value={form.assignedLocalAgent} onChange={(e) => set('assignedLocalAgent')(e.target.value)} /></div>
          </div>

          <div>
            <label className={lbl}>Status</label>
            <select className="input mt-1" value={form.status} onChange={(e) => set('status')(e.target.value)}>
              {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>

          <div>
            <label className={lbl}>Why{etaMoved && <span className="text-red-500"> *</span>}</label>
            <input
              className="input mt-1"
              value={form.notes}
              onChange={(e) => set('notes')(e.target.value)}
              placeholder={etaMoved ? 'e.g. berth congestion at Nhava Sheva, 4-day slip' : 'Optional note for the audit trail'}
            />
            {etaMoved && (
              <div className="mt-1 text-[11px] text-amber-700">
                The ETA is moving — other desks plan around it, so the reason is recorded with the change.
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <button className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-200" onClick={onClose}>Cancel</button>
          <button
            className="rounded-lg bg-blue-700 px-5 py-2 text-sm font-bold text-white hover:bg-blue-800 disabled:opacity-50"
            disabled={save.isPending || (etaMoved && form.notes.trim().length < 3)}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save sailing'}
          </button>
        </div>
      </div>
    </div>
  );
}
